import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Unstable_Grid2';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import html2canvas from 'html2canvas';
import { ASSETS_API, HOST_API } from 'src/config-global';
import { GridCloseIcon } from '@mui/x-data-grid';
import { useRouter } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
import { fCurrency } from 'src/utils/format-number';
import axiosInstance, { API_ENDPOINTS } from 'src/utils/axios';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Tabs from '@mui/material/Tabs';
import { Button, IconButton, ListItemText, Typography, Switch, FormControlLabel } from '@mui/material';
import { useBoolean } from 'src/hooks/use-boolean';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/iconify/iconify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TabPanel from './ementas-dish-tab';
import DishByCategoryListDialog from './dish-list-dialog';
import DishQuickForm from '../dish/dish-quick-edit-form';
import ManualDailyDishTab from './manual-daily-dish-tab';


const DAYS_OF_WEEK = [
  { value: 1, label: 'Segunda', labelFr: 'Lundi' },
  { value: 2, label: 'Terça', labelFr: 'Mardi' },
  { value: 3, label: 'Quarta', labelFr: 'Mercredi' },
  { value: 4, label: 'Quinta', labelFr: 'Jeudi' },
  { value: 5, label: 'Sexta', labelFr: 'Vendredi' },
  { value: 6, label: 'Sábado', labelFr: 'Samedi' },
];

export default function EmentasForm({ current, isEdit , refresh}) {
  const [currentTab, setCurrentTab] = useState(0);
  const [mainTab, setMainTab] = useState(0);
  const [currentDayTab, setCurrentDayTab] = useState(1); // 1 = Segunda
  const [hasPeriod, setHasPeriod] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualDishes, setManualDishes] = useState([]);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const router = useRouter();
  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);
  const handleChangeMainTab = useCallback((event, newValue) => {
    setMainTab(newValue);
  }, []);
  const handleChangeDayTab = useCallback((event, newValue) => {
    setCurrentDayTab(newValue);
  }, []);
  const { enqueueSnackbar } = useSnackbar();
  const [MenuTypesData,setMenuTypesData] = useState([]);
  const [DishCategoriesData,setDishCategoriesData] = useState([]);
  
  const fetchDataDishCategory = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.dishcategory.list);
      setDishCategoriesData(res.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchData = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.menutype.list);
      setMenuTypesData(res.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  useEffect(() => {
    fetchData();
    fetchDataDishCategory();
    // Se o menu já tem período definido, ativar o switch
    if (current?.startDate || current?.endDate) {
      setHasPeriod(true);
    }
    setIsManualMode(current?.isManualDailyDish || false);
    setManualDishes(current?.manualDishs || []);
  }, [current]);
  const schema = Yup.object().shape({
    menuType: Yup.object().shape({
      id: Yup.string().required("Is required"),
      portugueseName: Yup.string().required("Is required")
    }),
    category: Yup.array().of(Yup.object().shape({
      id: Yup.string().required("Is required"),
      portugueseName: Yup.string().required("Is required")
    })),
    dishs: Yup.array().of(Yup.object().shape({
      id: Yup.string().required("Is required"),
    })),
    startDate: Yup.date().nullable(),
    endDate: Yup.date().nullable(),
    portugueseNote: Yup.string().nullable(),
    frenchNote: Yup.string().nullable(),
    englishNote: Yup.string().nullable(),
  });
  const defaultData = useMemo(
    () => ({
      id: current ? current?.id : '',
      category: current ? current?.dishCategories : [],
      menuType: current ? current?.type : '',
      dishs: current ? current?.dishs : [],
      priority: current ? current?.priority : '',
      modifiedAt: current ? current?.modifiedAt : '',
      creationDate: current ? current?.creationDate : '',
      startDate: current?.startDate ? new Date(current.startDate) : null,
      endDate: current?.endDate ? new Date(current.endDate) : null,
      portugueseNote: current?.portugueseNote || '',
      frenchNote: current?.frenchNote || '',
      englishNote: current?.englishNote || '',
    }),
    [current]
  );
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultData,
  });
 
  const {
    watch,
    reset,
    setValue,
    getValues,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = useCallback(
    async (data) => {
      try {
        const categories = [];
        getValues().category.map((val) => categories.push(val.id));
        const createMenuDto = {
          portugueseName: '',
          menuTypeId: getValues().menuType.id,
          Categories: categories,
          startDate: getValues().startDate,
          endDate: getValues().endDate,
          portugueseNote: getValues().portugueseNote,
          frenchNote: getValues().frenchNote,
          englishNote: getValues().englishNote,
        }
        const obj = await axiosInstance.post(API_ENDPOINTS.menu.post, createMenuDto);
        router.push(paths.dashboard.ementas.edit(obj.data))
        reset();
        enqueueSnackbar(`${isEdit ? 'Atualização' : 'Adicionado'} com sucesso!`);
      } catch (error) {
        console.error(error);
      }
    },
    [enqueueSnackbar,  reset,isEdit,router,getValues]
  );


  function a11yProps(index) {
    return {
      id: `full-width-tab-${index}`,
      'aria-controls': `full-width-tabpanel-${index}`,
    };
  }

  const renderTabs = (
    <DragDropContext key="1" onDragEnd={(data) => handleDragEndCategory(data)}>
      <Droppable droppableId="tabs-categories" type="tab" direction="horizontal">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            <Tabs sx={{ pl: 1, pt: 1 }} value={currentTab} onChange={handleChangeTab}>
              {getValues().category.sort((a, b) => a.order - b.order).map((tab, index) => (
                <Draggable key={tab.id} draggableId={tab.id} index={index}  disableInteractiveElementBlocking>
                  {(provided1) => (
                  <Box component="div" onClick={() => setCurrentTab(index) }
                  ref={provided1.innerRef}
                  {...provided1.draggableProps}
                  {...provided1.dragHandleProps} >
                      <Tab
                      
                        key={tab.id}
                        label={tab.portugueseName}
                        sx={{
                          '&:not(:last-of-type)': {
                            mr: 3,
                          },
                          pl: 3,
                          pr: 3,
                        }}
                        {...a11yProps(index)}
                      /></Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Tabs>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
  const productSearch = useBoolean();
  const dishCreateDialog = useBoolean();
  const [currentCategory,setCurrentCategory] = useState({});
  const [currentCategoryDishList,setCurrentCategoryDishList] = useState([]);
  const values = watch();

  const { invoiceTo } = values;
  const  AddDishToList = async  (dish) => {
    if(isEdit){
      const id = current?.id;
      const dishId = dish.id;
      // Se hasPeriod está ativo, enviar o dia da semana selecionado no body
      const body = hasPeriod ? { dayOfWeek: currentDayTab } : {};
      const res = await axiosInstance.post(`${API_ENDPOINTS.menu.add_dish}/${id}/dish/${dishId}`, body);
      if(res.data.success === true){
        const data = await refresh();
      setValue("dishs",data.dishs)
      }
      enqueueSnackbar(res.data.message, {variant: !res.data.success ? 'error' : 'success'});

    }

  };
  const deleteDishFromMenu = async (dishId, dayOfWeek = null) => {
    if(isEdit){
      const id = current?.id;
      const dayOfWeekParam = dayOfWeek !== null ? `?dayOfWeek=${dayOfWeek}` : '';
      const res = await axiosInstance.delete(`${API_ENDPOINTS.menu.delete}/${id}/dish/${dishId}${dayOfWeekParam}`);
      enqueueSnackbar(res.data.message, {variant: !res.data.success ? 'error' : 'success'});
      const data = await refresh();
      setValue("dishs",data.dishs)
    }
  }
  const handleOnAddProduct = async (t) => {
    try {
      let res = null;
      res = await axiosInstance.get(`${API_ENDPOINTS.dish.list_by_category_id}${t.id}`);
      setCurrentCategoryDishList(res.data);
    } catch (error) {
      console.error(error);
    }
    setCurrentCategory({id:t.id,name: t.portugueseName});
    productSearch.onTrue();
  };

  const handleCloseDishCreate = async (createdDish) => {
    dishCreateDialog.onFalse();

    // Se um prato foi criado, adicionar automaticamente à ementa
    if (createdDish && createdDish.id && isEdit) {
      await AddDishToList(createdDish);
    }

    // Refrescar a lista de pratos da categoria atual
    if (currentCategory.id && currentCategory.id !== 'all') {
      try {
        const res = await axiosInstance.get(`${API_ENDPOINTS.dish.list_by_category_id}${currentCategory.id}`);
        setCurrentCategoryDishList(res.data);
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.dish.list);
        setCurrentCategoryDishList(res.data);
      } catch (error) {
        console.error(error);
      }
    }
  };
  
  const handleDragEnd = async (result,tab) => {
    console.log(result);
    const sourceOrder = getValues().dishs[result.source.index].order;
    const destOrder = getValues().dishs[result.destination.index].order;
    getValues().dishs[result.source.index].order = destOrder;
    getValues().dishs[result.destination.index].order = sourceOrder;
    setValue("dishs",getValues().dishs);
    const t = getValues().dishs.filter((dish) => dish.category.id === tab.id.toString()).map(obj => ({order:obj.order, dishId: obj.id}));
    await axiosInstance.put(`${API_ENDPOINTS.menu.edit}${getValues().id}/order`, t);
    const data = await refresh();
    setValue("dishs",data.dishs)
  };
  const handleDragEndCategory = async (result) => {
    console.log(result)
    const sourceOrder = getValues().category[result.source.index].order;
    const destOrder = getValues().category[result.destination.index].order;
    getValues().category[result.source.index].order = destOrder;
    getValues().category[result.destination.index].order = sourceOrder;
    const requestObj = getValues().category.map(obj => ({order:obj.order, categoryId: obj.id}))
    await axiosInstance.patch(`${API_ENDPOINTS.menu.edit}${getValues().id}/ordercat`, requestObj);
    const data = await refresh();
    setValue("category",data.dishCategories)
  };
  const renderTabPanels = (
    <>
    {getValues().category.length > 0 ? getValues().category.map((tab, index) => (
    <DragDropContext key={tab.id} onDragEnd={(result,a) => handleDragEnd(result,tab)}>
        <TabPanel value={currentTab} index={index} key={index}>
          <Button fullWidth
            size="medium"
            onClick={() => handleOnAddProduct(tab)}
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            Adicionar...
          </Button>
  
          <Droppable droppableId={`${tab.id}`} type="DISH">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {getValues().dishs.length > 0 ? getValues().dishs.sort((a, b) => a.order - b.order).map((dish, dishIndex) => {
                  if (dish.category?.id === tab.id) {
                    return (
                      <Draggable key={dish.id} draggableId={dish.id} index={dishIndex}>
                        {(provided1) => (
                            <Grid key={dish.id} ref={provided1.innerRef} {...provided1.draggableProps} {...provided1.dragHandleProps} 
                            xs={12} sm={12} md={12} lg={12} xl={12} sx={{ display: 'inline-flex', alignItems: 'center', p: 2 }}>
                              <Box  component="div" sx={{pr:4}}><span>#{dish.order}</span></Box>
                              <Avatar
                                alt={dish.portugueseName}
                                variant="rounded"
                                src={dish.photo !== "" ? `${ASSETS_API}${dish.photo}` : `${ASSETS_API}/static/dish/default_dish.png`}
                                sx={{ width: 54, height: 54, mr: 2 }}
                              />
  
                              <ListItemText
                                component="span"
                                disableTypography
                                primary={dish.portugueseName}
                                secondary={
                                  <Box component="span" sx={{ typography: 'body2', color: 'text.disabled' }}>
                                    <br/>{dish.category.portugueseName}
                                  </Box>
                                }
                              />
                              {fCurrency(dish.cost)} €
                              <IconButton aria-label="remove" onClick={() => deleteDishFromMenu(dish.id)} color="error">
                                <GridCloseIcon />
                              </IconButton>
                            </Grid>
                            )}
                      </Draggable> ) }
                      return null; }): null} {provided.placeholder}</div>)}
          </Droppable>
        </TabPanel>
    </DragDropContext>
      )) : null}</>

  );

  
  const handleOnChangeMenuType = async (item) => {
    setValue('menuType',item);
    const categories = [];
        getValues().category.map((val) => categories.push(val.id));
        const UpdateMenuDto = {
          menuTypeId: getValues().menuType.id,
          Categories: categories,
          startDate: getValues().startDate,
          endDate: getValues().endDate,
          portugueseNote: getValues().portugueseNote,
          frenchNote: getValues().frenchNote,
          englishNote: getValues().englishNote,
        }
        try {
          await axiosInstance.put(`${API_ENDPOINTS.menu.edit}${getValues().id}`, UpdateMenuDto);
          enqueueSnackbar(`${isEdit ? 'Atualização' : 'Adicionado'} com sucesso!`);
        } catch (error) {
          console.log(error);
        }
  }

  const handleOnChangeCategories = async (item) => {
    setValue('category',item);
    if(isEdit){
    const categories = [];
        getValues().category.map((val) => categories.push(val.id));
        const UpdateMenuDto = {
          menuTypeId: getValues().menuType.id,
          Categories: categories,
          startDate: getValues().startDate,
          endDate: getValues().endDate,
          portugueseNote: getValues().portugueseNote,
          frenchNote: getValues().frenchNote,
          englishNote: getValues().englishNote,
        }
        try {
          await axiosInstance.put(`${API_ENDPOINTS.menu.edit}${getValues().id}`, UpdateMenuDto);
          enqueueSnackbar(`${isEdit ? 'Atualização' : 'Adicionado'} com sucesso!`);
        } catch (error) {
          console.log(error);
        }
      }
  }
 const handleDelete = (e,d) => {
  const arrCata = getValues().category;
  const newArray = arrCata.filter((item) => item.id !== d.toString());
  const arrDishs = getValues().dishs;
  const newArrDishs = arrDishs.filter((dish) => dish.category !== d.toString())
  setValue('category',newArray);
  setValue('dishs',newArrDishs);
  handleOnChangeCategories(newArray);
  setCurrentTab(0);
 }

  const handleSavePeriodAndNotes = async () => {
    if (!isEdit) return;
    const categories = [];
    getValues().category.map((val) => categories.push(val.id));
    const UpdateMenuDto = {
      menuTypeId: getValues().menuType.id,
      Categories: categories,
      startDate: getValues().startDate,
      endDate: getValues().endDate,
      portugueseNote: getValues().portugueseNote,
      frenchNote: getValues().frenchNote,
      englishNote: getValues().englishNote,
    }
    try {
      await axiosInstance.put(`${API_ENDPOINTS.menu.edit}${getValues().id}`, UpdateMenuDto);
      enqueueSnackbar('Período e notas atualizados com sucesso!');
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Erro ao atualizar', { variant: 'error' });
    }
  }

  const handleToggleManualMode = async (checked) => {
    setIsManualMode(checked);
    if (!isEdit) return;
    try {
      await axiosInstance.put(`${API_ENDPOINTS.menu.daily_dish_mode}/${getValues().id}/daily-dish-mode`, { isManual: checked });
      enqueueSnackbar('Modo do Prato do Dia atualizado.');
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Erro ao mudar o modo', { variant: 'error' });
    }
  };

  // Refresca os dados do menu e devolve os pratos manuais atualizados
  const refreshManual = async () => {
    const data = await refresh();
    setManualDishes(data?.manualDishs || []);
    return data;
  };

  const handleDownloadMenuImage = async () => {
    setIsGeneratingImage(true);
    try {
      // Buscar dados do menu da API
      const res = await axiosInstance.get(`${API_ENDPOINTS.menu.list}/daily-dish`);
      const data = res.data;

      if (!data) {
        enqueueSnackbar('Nenhum menu com período encontrado', { variant: 'error' });
        return;
      }

      const dayNames = {
        1: 'Lundi',
        2: 'Mardi',
        3: 'Mercredi',
        4: 'Jeudi',
        5: 'Vendredi',
        6: 'Samedi',
        0: 'Dimanche'
      };

      const allergenMap = (name) => {
        const lower = name?.toLowerCase() || '';
        if (lower.includes('gluten')) return '1';
        if (lower.includes('crustac')) return '2';
        if (lower.includes('oeuf') || lower.includes('egg')) return '3';
        if (lower.includes('poisson') || lower.includes('fish')) return '4';
        if (lower.includes('arachide') || lower.includes('peanut')) return '5';
        if (lower.includes('soja') || lower.includes('soy')) return '6';
        if (lower.includes('lait') || lower.includes('milk')) return '7';
        if (lower.includes('coque') || lower.includes('nut')) return '8';
        if (lower.includes('celeri') || lower.includes('céleri')) return '9';
        if (lower.includes('moutarde') || lower.includes('mustard')) return '10';
        if (lower.includes('sesame') || lower.includes('sésame')) return '11';
        if (lower.includes('sulfite') || lower.includes('sulph')) return '12';
        if (lower.includes('lupin')) return '13';
        if (lower.includes('mollusque') || lower.includes('mollus')) return '14';
        return '';
      };

      // Agrupar pratos por dia
      const dishesByDay = {};
      data.dishes?.forEach(dish => {
        const day = dish.dayOfWeek;
        if (!dishesByDay[day]) dishesByDay[day] = [];
        dishesByDay[day].push(dish);
      });

      // Gerar HTML dos pratos
      let dishesHtml = '';
      [1, 2, 3, 4, 5, 6].forEach(day => {
        if (dishesByDay[day] && dishesByDay[day].length > 0) {
          dishesHtml += `<div style="color: #c41e3a; font-size: 18px; font-weight: bold; margin-top: 15px; margin-bottom: 5px;">${dayNames[day]}</div>`;
          dishesByDay[day].forEach(dish => {
            const allergenNums = dish.allergens?.map(a => allergenMap(a.frenchName)).filter(n => n).join('.');
            const allergenText = allergenNums ? `(${allergenNums})` : '';
            dishesHtml += `<div style="font-size: 14px; color: #333; margin-bottom: 3px; line-height: 1.4;">${dish.frenchName}/${dish.portugueseName}${allergenText}</div>`;
          });
        }
      });

      const startDate = data.startDate ? new Date(data.startDate).toLocaleDateString('pt-PT') : '';
      const endDate = data.endDate ? new Date(data.endDate).toLocaleDateString('pt-PT') : '';

      // Criar elemento temporário
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.innerHTML = `
        <div id="menu-to-capture" style="width: 600px; padding: 30px; background: white; font-family: Georgia, serif; text-align: center; position: relative; overflow: hidden;">
          <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 450px; height: 450px; z-index: 0; display: flex; align-items: center; justify-content: center;">
            <img src="https://fleurdeselrestaurant.lu/img/inicio/logo_hist.png" style="width: 100%; height: 100%; object-fit: contain; opacity: 0.12;" crossorigin="anonymous" />
          </div>
          <div style="position: relative; z-index: 1;">
            <img src="https://fleurdeselrestaurant.lu/img/inicio/logo_hist.png" style="width: 100px; margin-bottom: 10px;" crossorigin="anonymous" />
            <div style="font-size: 24px; font-weight: bold; color: #333; margin-bottom: 5px;">Menu de Midi Lundi au Samedi de 11h30 à 14h</div>
            <div style="font-size: 18px; font-weight: bold; color: #333; margin-bottom: 20px;">Du ${startDate} au ${endDate}</div>
            ${dishesHtml}
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #c41e3a;">
              <div style="color: #1e3a5f; font-size: 16px; text-decoration: underline; margin-bottom: 15px;">Plat du Jour Rodizio du Lundi au Samedi</div>
              <div style="font-weight: bold; font-size: 14px; color: #333; margin-bottom: 5px;">TOUS LES MENUS INCLUENT SOUPE OU DESSERT</div>
              <div style="color: #c41e3a; font-size: 16px; font-weight: bold; margin-top: 10px;">PLAT DU JOUR RODIZIO (VIANDES + HARICOTS + RIZ) 18,00€</div>
              <div style="color: #c41e3a; font-size: 16px; font-weight: bold; margin-top: 10px;">SUGGESTIONS DU JOUR 15,00€</div>
              <div style="font-size: 12px; color: #c41e3a; margin-top: 15px; font-weight: bold;">RÉSERVATION AU 28 37 19 79 / 25, RUE JOSEPH JUNCK</div>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(container);

      // Esperar imagens carregarem
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = container.querySelector('#menu-to-capture');
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Download
      const link = document.createElement('a');
      const fileName = `menu-fleur-de-sel-${startDate.replace(/\//g, '-')}-${endDate.replace(/\//g, '-')}.png`;
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      link.click();

      // Limpar
      document.body.removeChild(container);
      enqueueSnackbar('Imagem gerada com sucesso!', { variant: 'success' });
    } catch (error) {
      console.error('Error generating image:', error);
      enqueueSnackbar('Erro ao gerar imagem', { variant: 'error' });
    } finally {
      setIsGeneratingImage(false);
    }
  }

//  //todo: show the dishs selected by cat
  const renderProperties = (
   
    <>
      <Grid  item md={12} sm={12} xs={12}>
        <Card>
          <CardHeader title="Dados Gerais" />
          <Box
          rowGap={2}
          columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
             sx={{ p: 3 }}
          >
             {/* <RHFTextField name="portugueseName" label="Nome (português)" />
            <RHFTextField name="frenchName" label="Nome (francês)" />
            <RHFTextField name="englishName" label="Nome (inglês)" /> */}
            {isEdit ? 
            <><RHFAutocomplete
              name="menuType"
              label="Tipo de Menu"
              onChange={(ev,a) => handleOnChangeMenuType(a)}
              options={MenuTypesData.map((option) => option)}
              getOptionLabel={(option) => option.portugueseName || ''}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.portugueseName}
                </li>
              )}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                 
                    key={option.id}
                    label={option?.portugueseName}
                    size="small"
                    color="info"
                    variant="soft"
                  />
                ))
              }
            />   </> : <RHFAutocomplete
            name="menuType"
            label="Tipo de Menu"
            options={MenuTypesData.map((option) => option)}
            getOptionLabel={(option) => option.portugueseName || ''}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.portugueseName}
              </li>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
               
                  key={option.id}
                  label={option?.portugueseName}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
          />}
              <RHFAutocomplete
              name="category"
              label="Categorias de pratos"
              multiple
              onChange={(e,a) => handleOnChangeCategories(a)}
              options={DishCategoriesData.map((option) => option)}
              getOptionLabel={(option) => option.portugueseName || ''}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.portugueseName}
                </li>
              )}
              // onChange={(baseEvent,dataArray,action, option) => handleChangeDataItems(action, option)}
              renderTags={(selected, getTagProps) =>
                  (selected.map((option, index) => (
                  <Chip
                    {...getTagProps({ index }) }
                    key={option.id}
                    onDelete={(e) => handleDelete(e,option.id.toString())}
                    label={option?.portugueseName}
                    size="small"
                    color="primary"
                    variant="soft"
                  />
                )))
              }
            />

            {isEdit && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasPeriod}
                      onChange={(e) => setHasPeriod(e.target.checked)}
                    />
                  }
                  label="Definir período de validade"
                />
              </Box>
            )}

          </Box>

        </Card>

        {/* Tabs principais - Dias da Semana e Período/Notas */}
        {isEdit && hasPeriod && (
          <Card sx={{ marginTop: 3 }}>
            <Tabs value={mainTab} onChange={handleChangeMainTab} sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Pratos por Dia" />
              <Tab label="Período e Notas" />
            </Tabs>
          </Card>
        )}

        {/* Tab de Período e Notas */}
        {isEdit && hasPeriod && mainTab === 1 && (
          <Card sx={{ marginTop: 3 }}>
            <CardHeader title="Período de Validade e Notas" />
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Defina o período de validade do menu (ex: Prato do Dia válido de 23/06 a 28/06)
              </Typography>
              <Box
                rowGap={2}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                }}
                sx={{ mb: 3 }}
              >
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Data de Início"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Data de Fim"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Notas do menu (ex: &quot;TODOS OS MENUS INCLUEM SOPA OU SOBREMESA&quot;)
              </Typography>
              <Box
                rowGap={2}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: '1fr',
                }}
              >
                <RHFTextField
                  name="portugueseNote"
                  label="Nota (Português)"
                  multiline
                  rows={2}
                />
                <RHFTextField
                  name="frenchNote"
                  label="Nota (Francês)"
                  multiline
                  rows={2}
                />
                <RHFTextField
                  name="englishNote"
                  label="Nota (Inglês)"
                  multiline
                  rows={2}
                />
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <LoadingButton
                  variant="outlined"
                  color="info"
                  onClick={handleDownloadMenuImage}
                  loading={isGeneratingImage}
                  startIcon={<Iconify icon="solar:download-bold" />}
                >
                  Download Imagem
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  onClick={handleSavePeriodAndNotes}
                >
                  Guardar Período e Notas
                </LoadingButton>
              </Box>
            </Box>
          </Card>
        )}

        {/* Tab de Pratos por Dia da Semana (quando hasPeriod está ativo) */}
        {isEdit && hasPeriod && mainTab === 0 && (
          <Card sx={{ marginTop: 3 }}>
            <Box sx={{ px: 2, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isManualMode}
                    onChange={(e) => handleToggleManualMode(e.target.checked)}
                  />
                }
                label="Modo Inserção Manual"
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {isManualMode
                  ? 'Escreva os pratos à mão (sem usar as parametrizações).'
                  : 'A escolher pratos das parametrizações.'}
              </Typography>
            </Box>
            <Tabs value={currentDayTab} onChange={handleChangeDayTab} sx={{ px: 2, pt: 1 }}>
              {DAYS_OF_WEEK.map((day) => (
                <Tab key={day.value} value={day.value} label={day.label} />
              ))}
              <Tab
                value={-1}
                label="Lixo"
                sx={{ color: 'error.main' }}
                icon={<Iconify icon="solar:trash-bin-trash-bold" width={18} />}
                iconPosition="start"
              />
            </Tabs>
            <Box sx={{ p: 2 }}>
              {/* Conteúdo do Tab Lixo */}
              {currentDayTab === -1 ? (
                <>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Pratos sem dia da semana ou sem categoria válida. Estes pratos devem ser eliminados.
                  </Typography>
                  {getValues().dishs?.filter((dish) => dish.dayOfWeek == null || dish.category == null).length > 0 ? (
                    getValues().dishs
                      .filter((dish) => dish.dayOfWeek == null || dish.category == null)
                      .map((dish) => (
                        <Grid
                          key={`trash-${dish.id}-${dish.dayOfWeek}`}
                          xs={12}
                          sx={{ display: 'inline-flex', alignItems: 'center', p: 2, width: '100%', bgcolor: 'error.lighter', borderRadius: 1, mb: 1 }}
                        >
                          <Avatar
                            alt={dish.portugueseName}
                            variant="rounded"
                            src={dish.photo !== "" ? `${ASSETS_API}${dish.photo}` : `${ASSETS_API}/static/dish/default_dish.png`}
                            sx={{ width: 54, height: 54, mr: 2 }}
                          />
                          <ListItemText
                            component="span"
                            disableTypography
                            primary={dish.portugueseName}
                            secondary={
                              <Box component="span" sx={{ typography: 'body2', color: 'text.disabled' }}>
                                {dish.dayOfWeek == null ? 'Sem dia' : ''} {dish.category == null ? '| Sem categoria' : ''}
                              </Box>
                            }
                          />
                          {fCurrency(dish.cost)} €
                          <IconButton aria-label="remove" onClick={() => deleteDishFromMenu(dish.id, dish.dayOfWeek)} color="error">
                            <GridCloseIcon />
                          </IconButton>
                        </Grid>
                      ))
                  ) : (
                    <Typography sx={{ p: 2, textAlign: 'center', color: 'success.main' }}>
                      Não há pratos órfãos. Tudo limpo!
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  {isManualMode && (
                    <ManualDailyDishTab
                      menuId={getValues().id}
                      dayOfWeek={currentDayTab}
                      dayLabel={DAYS_OF_WEEK.find((d) => d.value === currentDayTab)?.label}
                      dishes={manualDishes}
                      refresh={refreshManual}
                    />
                  )}
                  {!isManualMode && (
                  <>
                  <Button
                    fullWidth
                    size="medium"
                    onClick={() => {
                      setCurrentCategory({ id: 'all', name: 'Todos' });
                      productSearch.onTrue();
                      // Buscar todos os pratos
                      axiosInstance.get(API_ENDPOINTS.dish.list).then((res) => {
                        setCurrentCategoryDishList(res.data);
                      });
                    }}
                    color="primary"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                  >
                    Adicionar Prato para {DAYS_OF_WEEK.find(d => d.value === currentDayTab)?.label}
                  </Button>

                  {/* Lista de pratos do dia selecionado */}
                  {getValues().dishs?.filter((dish) => dish.dayOfWeek === currentDayTab).length > 0 ? (
                    getValues().dishs
                      .filter((dish) => dish.dayOfWeek === currentDayTab)
                      .sort((a, b) => a.order - b.order)
                      .map((dish) => (
                        <Grid
                          key={dish.id}
                          xs={12}
                          sx={{ display: 'inline-flex', alignItems: 'center', p: 2, width: '100%' }}
                        >
                          <Avatar
                            alt={dish.portugueseName}
                            variant="rounded"
                            src={dish.photo !== "" ? `${ASSETS_API}${dish.photo}` : `${ASSETS_API}/static/dish/default_dish.png`}
                            sx={{ width: 54, height: 54, mr: 2 }}
                          />
                          <ListItemText
                            component="span"
                            disableTypography
                            primary={dish.portugueseName}
                            secondary={
                              <Box component="span" sx={{ typography: 'body2', color: 'text.disabled' }}>
                                {dish.frenchName}
                              </Box>
                            }
                          />
                          {fCurrency(dish.cost)} €
                          <IconButton aria-label="remove" onClick={() => deleteDishFromMenu(dish.id, dish.dayOfWeek)} color="error">
                            <GridCloseIcon />
                          </IconButton>
                        </Grid>
                      ))
                  ) : (
                    <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      Nenhum prato adicionado para {DAYS_OF_WEEK.find(d => d.value === currentDayTab)?.label}
                    </Typography>
                  )}
                  </>
                  )}
                </>
              )}
            </Box>
          </Card>
        )}

        {/* Tab de Pratos por Categorias (modo normal, sem período) */}
        <Card sx={{ marginTop: 3, display: (!hasPeriod && isEdit) ? 'block' : 'none' }}>
            {getValues().category !== null && getValues().category.length > 0 && isEdit ? renderTabs : ''}
            {getValues().category !== null && getValues().category.length > 0  && isEdit ? renderTabPanels : ''}
        </Card>
      </Grid>

      <DishByCategoryListDialog
        data={currentCategory}
        title={`Lista de Produtos ${currentCategory.name}`}
        open={productSearch.value}
        selected={(selectedId) => invoiceTo?.id === selectedId}
        onSelect={(dish) =>  AddDishToList(dish)}
        onClose={productSearch.onFalse}
        list={currentCategoryDishList}
        action={
          <Button
            size="small"
            startIcon={<Iconify icon="mingcute:add-line" />}
            sx={{ alignSelf: 'flex-end' }}
            onClick={() => dishCreateDialog.onTrue()}
          >
            Criar
          </Button>
      }
    />

      <DishQuickForm
        open={dishCreateDialog.value}
        onClose={handleCloseDishCreate}
        isEdit={false}
        current={{}}
      /></>
  );

  
  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid  container>
        {renderProperties}
      </Grid>
      <Box sx={{mt:3}}>
      {!isEdit ? 
      <Box component="div">
      <Button variant="outlined" onClick={() => {console.log(getValues())}}>Cancelar</Button>
      <LoadingButton sx={{ml:2}} type="submit" variant="contained" loading={isSubmitting}>{isEdit ? 'Atualizar Ementa' : 'Criar Ementa'}</LoadingButton></Box> : null}</Box> 
    </FormProvider> 
  );
}

EmentasForm.propTypes = {
  current: PropTypes.object,
  isEdit: PropTypes.bool,
  refresh: PropTypes.func
};

